import { ChangeEvent, useState } from "react";
import { useAppDispatch } from "./app/hooks";
import {
    Alert,
    Backdrop,
    Box,
    Button,
    CircularProgress,
    Divider,
    Grid,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
} from "@mui/material";
import { Localized, useLocalization } from "@fluent/react";
import { showError, showSuccess } from "./features/notifications/notificationsSlice";
import { changePassword } from "./features/user/userSlice";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { AuthPageLayout } from "./AuthPageLayout";
import { LanguageSelector } from "./LanguageSelector";


export interface LoginAPIError {
    success: boolean
    token: null | string
    errors: string[]
}

export const ChangePassword = () => {
    const dispatch = useAppDispatch()
    const { l10n } = useLocalization()

    const [oldPassword, setOldPassword] = useState("");
    const [password, setPassword] = useState("");
    const [passwordConfirm, setPasswordConfirm] = useState("");
    const [email, setEmail] = useState("");

    const [errorPassword, setPasswordError] = useState("");
    const [errorPasswordList, setErrorPasswordList] = useState<{ title: string; params?: { count?: number } }[]>([]);
    const [emailError, setEmailError] = useState("");

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [showOldPassword, setShowOldPassword] = useState(false);

    const [mask, setMask] = useState<boolean>(false);
    const navigate = useNavigate()

    const onOldPasswordChange = (e: ChangeEvent<HTMLInputElement>) => setOldPassword(e.target.value)
    const onPasswordChange = (e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)
    const onPasswordConfirmChange = (e: ChangeEvent<HTMLInputElement>) => setPasswordConfirm(e.target.value)
    const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)

    const toggleOldPasswordVisibility = () => {
        setShowOldPassword(!showOldPassword);
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const toggleConfirmPasswordVisibility = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };


    function timeout(delay: number) {
        return new Promise(res => setTimeout(res, delay));
    }

    const validateEmail = () => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setEmailError("Nieprawidłowy adres e-mail");
        }
        else {
            setEmailError("");
        }
    };

    const clearErrors = () => {
        setPasswordError("")
    }

    const Validate = () => {
        let valResult = true;
        setErrorPasswordList([]);
        if (email === '') {
            valResult = false;
        }
        if (password !== '' && passwordConfirm !== '' && passwordConfirm !== password) {
            valResult = false;
            setPasswordError(l10n.getString("password-do-not-match", null, "_password not match"));
        }
        return valResult;
    }

    const handleSaveClick = async () => {

        clearErrors()
        if (!Validate()) {

            return;
        }


        try {
            setMask(true)

            const resultAction = await dispatch(changePassword({
                email: email,
                oldPassword: oldPassword,
                newPassword: password
            }))

            if (changePassword.fulfilled.match(resultAction)) {
                dispatch(showSuccess("password-changed-sucesfully"))
                await timeout(2000)
                navigate("/login", { replace: true })
            }

            if (changePassword.rejected.match(resultAction)) {
                const { payload } = resultAction
                if (payload) {
                    switch (payload.kind) {
                        case "http":
                            if (payload.body) {
                                const errors = JSON.parse(payload.body);
                                if(Array.isArray(errors)) {
                                    const passwordErrors = errors.filter((err: any) => err.title.startsWith('password-require'));
                                    setErrorPasswordList(passwordErrors);
                                } else {
                                    const otherErrors = errors.errors.filter((err: any) => !err.startsWith('password-require'));
                                    dispatch(showError(otherErrors[0]));
                                }
                            }
                            else {
                                dispatch(showError("error"))
                            } break
                        case "connection":
                            dispatch(showError("connection-issue"))
                            break
                    }
                } else {
                    dispatch(showError("error"))
                }
            }

        }
        catch (error) {
            dispatch(showError(error ?? "error-login-failed"))
        }
        finally {
            setMask(false)
        }


    }
    return (
        <Box>
            <AuthPageLayout>
                <Grid container direction="column" spacing={2}>
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" fontWeight="bold" textAlign="center">
                            <Localized id="password-change-required">
                                <span>Wymagana zmiana hasła</span>
                            </Localized>
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                            <Localized id="set-new-password">
                                <span>Ustal nowe hasło</span>
                            </Localized>
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            value={email}
                            required
                            onBlur={validateEmail}
                            error={!!emailError}
                            helperText={emailError}
                            type="email"
                            onChange={handleEmailChange}
                            inputProps={{ maxLength: 100, autoComplete: "email" }}
                            label={<Localized id="user-email">Email</Localized>}>
                        </TextField>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            type={showOldPassword ? 'text' : 'password'}
                            onBlur={clearErrors}
                            onChange={onOldPasswordChange}
                            required
                            error={!oldPassword}
                            label={l10n.getString("password-current", null, "Obecne hasło")}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton tabIndex={-1} onClick={toggleOldPasswordVisibility}>
                                            {showOldPassword ? <Visibility /> : <VisibilityOff />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            type={showPassword ? 'text' : 'password'}
                            onBlur={clearErrors}
                            onChange={onPasswordChange}
                            error={!!errorPassword || errorPasswordList.length > 0}
                            helperText={errorPasswordList.length > 0 ? l10n.getString("password-policy", null, "_Polityka bezpieczeństwa firmy wymaga, aby hasło spełniało poniższe kryteria") : errorPassword}
                            label={l10n.getString("new-password", null, "Nowe hasło")}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton tabIndex={-1} onClick={togglePasswordVisibility}>
                                            {showPassword ? <Visibility /> : <VisibilityOff />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            type={showConfirmPassword ? 'text' : 'password'}
                            onChange={onPasswordConfirmChange}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton tabIndex={-1} onClick={toggleConfirmPasswordVisibility}>
                                            {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            label={l10n.getString("confirm-password", null, "_Potwierdź hasło")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        {errorPasswordList.length > 0 && (
                            <Alert 
                                severity="error" 
                                variant="standard"
                            >
                                <Box 
                                    component="ul" 
                                    sx={{ 
                                        m: 0, 
                                        pl: '20px',
                                        textAlign: 'left', 
                                        display: 'flex', 
                                        flexDirection: 'column', 
                                        gap: '4px',
                                        fontSize: '0.875rem' 
                                    }}
                                >
                                    {errorPasswordList.map((err, index) => (
                                    <Box component="li" key={index} sx={{ color: 'inherit' }}>
                                        <Typography variant="body2" component="span">
                                        <Localized 
                                            id={err.title} 
                                            vars={{ ...(err.params || {}) }} 
                                        />
                                        </Typography>
                                    </Box>
                                    ))}
                                </Box>
                            </Alert>
                        )}
                    </Grid>
                    <Grid item xs={12}>
                        <Button onClick={handleSaveClick} color="primary" size="large" variant="contained" fullWidth>
                            <Localized id="save">
                                <span>Zapisz</span>
                            </Localized>
                        </Button>
                    </Grid>

                    <Divider style={{ marginLeft: 30, marginRight: 20, marginTop: 10, marginBottom: 20 }} />
                    <Grid item textAlign="center">
                        <LanguageSelector />
                    </Grid>
                </Grid>
                <Backdrop sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, color: '#fff' }} open={mask}>
                    <CircularProgress color="inherit"></CircularProgress>
                </Backdrop>
            </AuthPageLayout>
        </Box>
    );

}
